import React, { useState } from 'react';
import { Form, Select, Tooltip } from 'antd';
import type { Rule } from 'antd/es/form';
import type { SelectProps } from 'antd/es/select';

interface DropdownProps<T = any> {
    name?: string;
    label?: string;
    tooltip?: string;
    rules?: Rule[];
    placeholder?: string;
    required?: boolean;
    showSearch?: boolean;
    allowClear?: boolean;
    disabled?: boolean;
    includeEmptyOption?: boolean;
    allowCustomValue?: boolean; // <-- yeni prop

    dataSource: T[];
    labelKey: keyof T;
    valueKey: keyof T;

    onChange?: (value: T[keyof T] | string | null, option?: any) => void;
    value?: T[keyof T] | string | null;

    dropdownStyle?: React.CSSProperties;
    style?: React.CSSProperties;
    onFocus?: React.FocusEventHandler<any>;
}

const Dropdown = <T extends object>({
    name,
    label,
    tooltip,
    rules = [],
    placeholder,
    showSearch = true,
    allowClear = true,
    disabled = false,
    includeEmptyOption = false,
    allowCustomValue = false, // <-- default false
    dataSource,
    labelKey,
    valueKey,
    style,
    dropdownStyle,
    onChange,
    value,
    onFocus,
}: DropdownProps<T>) => {
    const [customOptions, setCustomOptions] = useState<string[]>([]);

    const labelContent = tooltip ? <Tooltip title={tooltip}>{label}</Tooltip> : label;

    const filterOption: SelectProps<any>['filterOption'] = (input, option) =>
        (option?.children as unknown as string).toLowerCase().includes(input.toLowerCase());

    // Eğer allowCustomValue true ise Select'i combobox modunda aç
    return (
        <Select
            mode={allowCustomValue ? undefined : undefined}
            showSearch={showSearch}
            allowClear={allowClear}
            disabled={disabled}
            filterOption={filterOption}
            style={{ width: '100%', ...style }}
            dropdownStyle={dropdownStyle}
            placeholder={placeholder || String(label)}
            onChange={(val, option) => onChange?.(val as T[keyof T] | string, option)}
            value={value}
            onFocus={onFocus}
            onBlur={(e) => {
                const inputValue = (e.target as HTMLInputElement).value;

                if (
                    allowCustomValue &&
                    inputValue &&
                    !dataSource.some(item => String(item[labelKey]).toLowerCase() === inputValue.toLowerCase()) &&
                    !customOptions.includes(inputValue)
                ) {
                    setCustomOptions(prev => [...prev, inputValue]);
                    onChange?.(inputValue, null);
                }
            }}
            notFoundContent={allowCustomValue ? null : undefined}
            onInputKeyDown={allowCustomValue ? (e) => {
                if (e.key === 'Enter') {
                    const inputValue = (e.target as HTMLInputElement).value;
                    if (
                        inputValue &&
                        !dataSource.some(item => String(item[labelKey]).toLowerCase() === inputValue.toLowerCase()) &&
                        !customOptions.includes(inputValue)
                    ) {
                        setCustomOptions(prev => [...prev, inputValue]);
                        onChange?.(inputValue, null);
                    }
                }
            } : undefined}
        >
            {includeEmptyOption && <Select.Option value={null}>{''}</Select.Option>}
            {dataSource?.map((item, index) => (
                <Select.Option key={`${String(item[valueKey])}-${index}`} value={item[valueKey] as string | number}>
                    {item[labelKey] as string}
                </Select.Option>
            ))}
            {allowCustomValue && customOptions.map((opt, idx) => (
                <Select.Option key={`custom-${idx}`} value={opt}>
                    {opt}
                </Select.Option>
            ))}
        </Select>
    );
};

export default Dropdown;
